import { useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { BellIcon, FileTextIcon, LayoutGridIcon } from 'lucide-react';
import { useSnapshot } from 'valtio/react';

import { EvidencePreview } from '@paid-ui/blocks/evidence-preview';
import { GlobalAlertDialog } from '@paid-ui/blocks/global-alert';
import { GlobalConfirmDialog } from '@paid-ui/blocks/global-confirm';
import { GlobalDialog } from '@paid-ui/blocks/global-dialog';
import { GlobalFlyout } from '@paid-ui/blocks/global-flyout';
import { LoadingIndicator } from '@paid-ui/components/loading-indicator';
import { Logo } from '@paid-ui/components/logo';
import { UnstyledButton } from '@paid-ui/components/unstyled-button';
import { AccountIcon } from '@paid-ui/icons/account';
import { ClaimsIcon } from '@paid-ui/icons/claims';
import { ProjectsIcon } from '@paid-ui/icons/projects';
import { SettingsIcon } from '@paid-ui/icons/settings';
import { appConfigManager, getMaintenance } from '@paid-ui/models/app-config';
import { savingsAccountManager } from '@paid-ui/models/savings-account';
import { transactionAccountManager } from '@paid-ui/models/transaction-account';
import { userManager } from '@paid-ui/models/user';
import { userGroupManager } from '@paid-ui/models/user-group';
import { useGetRequiringActions } from '@paid-ui/queries/requiring-actions';
import {
  useSavingsAccountSetup,
  useTransactionAccountSetup,
} from '@paid-ui/queries/transaction-account';
import { Banner, Flex, styled } from '@paid-ui/ui';
import {
  isAcknowledgedRequiringAction,
  isVisibleRequiringAction,
} from '@paid-ui/utils/requiring-action';

import { CountIndicator } from './count-indicator';
import { HamburgerMenu } from './hamburger-menu';
import { HelpCenter } from './help-center';
import { Navbar } from './navbar';
import { NavbarLink } from './navbar-link';
import { UserCenter } from './user-center';

// Module-level decision flag. Reset on every full page load (sign-in
// redirect, refresh, direct URL) and preserved across in-app navigations.
// This makes the landing gate fire on page load — including a refresh of `/`
// — but not when the user manually clicks the Dashboard nav from elsewhere
// in the app.
let hasResolvedLandingDecision = false;

export const BannerContentContainer = styled(Flex, {
  'color': 'white',
  'alignItems': 'start',
  'flexDirection': 'column',
  'gap': '8px',
  '@md': {
    gap: '18px',
    alignItems: 'center',
    flexDirection: 'unset',
  },
});

interface Props {
  children: React.ReactNode;
  className?: string;
}

export const BasicLayout: React.FC<Props> = ({ children }) => {
  const pathname = usePathname();
  const router = useRouter();
  const { announcements } = useSnapshot(appConfigManager);
  const { isAccountViewable, disableContractActions } = useSnapshot(userManager);
  const maintenance = getMaintenance(announcements);
  const enableAccount = isAccountViewable;

  // When the transaction-history flyout is open for a specific account, only
  // refresh that account's summary. Otherwise (e.g. on the accounts overview),
  // both summaries are fetched.
  const { accountId: transactionAccountId } = useSnapshot(transactionAccountManager);
  const { accountId: savingsAccountId } = useSnapshot(savingsAccountManager);
  const isHistoryDialog = router.query.dialog === 'transaction-history';
  const queryAccountId =
    typeof router.query.accountId === 'string' ? router.query.accountId : undefined;
  const viewingTransaction = isHistoryDialog && queryAccountId === transactionAccountId;
  const viewingSavings = isHistoryDialog && queryAccountId === savingsAccountId;
  const enableTransactionSummary = enableAccount && (!isHistoryDialog || viewingTransaction);
  const enableSavingsSummary = enableAccount && (!isHistoryDialog || viewingSavings);

  useTransactionAccountSetup(enableTransactionSummary);
  useSavingsAccountSetup(enableSavingsSummary);

  const isLandingRoute = pathname === '/' || pathname === '/activity';
  const { data: requiringActions, isFetched: isRequiringActionsFetched } = useGetRequiringActions();
  const shouldGateForRequiringActions =
    !hasResolvedLandingDecision &&
    isLandingRoute &&
    !disableContractActions &&
    !isRequiringActionsFetched;

  useEffect(() => {
    if (hasResolvedLandingDecision) return;
    if (!isLandingRoute) {
      // First BasicLayout mount in this page load happened on a non-landing
      // path (e.g. /account, sign-in with ?next=/foo, refresh on /projects).
      // Record the decision as resolved so a later in-app nav to / or
      // /activity does not re-trigger the gate.
      hasResolvedLandingDecision = true;
      return;
    }
    if (disableContractActions) {
      hasResolvedLandingDecision = true;
      return;
    }
    if (!isRequiringActionsFetched) return;
    hasResolvedLandingDecision = true;
    const hasVisibleAction = (requiringActions ?? [])
      .filter(isVisibleRequiringAction)
      .some((action) => !isAcknowledgedRequiringAction(action));
    const targetPathname = hasVisibleAction ? '/activity' : '/';
    if (targetPathname !== pathname) {
      router.replace({ pathname: targetPathname, query: router.query });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLandingRoute, isRequiringActionsFetched, disableContractActions]);

  return (
    <div className="grid min-h-screen sm:grid-cols-[80px_1fr]">
      <Navbar side="left">
        <UnstyledButton
          as="a"
          href="/"
          className="flex w-full shrink-0 items-center justify-center py-6"
        >
          <Logo width={30} symbolOnly />
        </UnstyledButton>
        <nav className="flex w-full flex-1 flex-col overflow-y-auto scrollbar-hide">
          <NavbarLink
            href="/activity"
            className="group"
            isActive={pathname.startsWith('/activity')}
          >
            <CountIndicator />
            <BellIcon className="h-5 w-5" />
            <span className="text-xs">Activity</span>
          </NavbarLink>
          <NavbarLink href="/" className="group" isActive={pathname === '/'}>
            <LayoutGridIcon className="h-5 w-5" />
            <span className="text-xs">Dashboard</span>
          </NavbarLink>
          <NavbarLink
            href="/projects"
            className="group"
            isActive={pathname.startsWith('/projects')}
          >
            <ProjectsIcon className="h-5 w-5" />
            <span className="text-xs">Projects</span>
          </NavbarLink>
          <NavbarLink
            href="/contracts"
            className="group"
            isActive={pathname.startsWith('/contracts')}
          >
            <FileTextIcon className="h-5 w-5" />
            <span className="text-xs">Contracts</span>
          </NavbarLink>
          <NavbarLink href="/claims" className="group" isActive={pathname.startsWith('/claims')}>
            <ClaimsIcon className="h-5 w-5" />
            <span className="text-xs">Claims</span>
          </NavbarLink>
          {/* Always reachable: the page itself decides between the accounts
              overview, the not-ready screen and the no-permission screen. */}
          <NavbarLink href="/account" isActive={pathname.startsWith('/account')}>
            <AccountIcon className="h-5 w-5" />
            <span className="text-xs">Account</span>
          </NavbarLink>
          <div className="mt-auto">
            <div className="p-1.5">
              <HelpCenter />
            </div>
            <NavbarLink
              href="/settings"
              className="group"
              isActive={pathname.startsWith('/settings')}
            >
              <SettingsIcon className="h-5 w-5" />
              <span className="text-xs">Settings</span>
            </NavbarLink>
          </div>
        </nav>
        <div className="shrink-0 gap-x-6 py-4">
          <UserCenter />
        </div>
      </Navbar>
      <Navbar side="top" fullWidth>
        <div className="flex items-center gap-x-6">
          <UnstyledButton as="a" href="/" className="shrink-0">
            <Logo width={30} symbolOnly />
          </UnstyledButton>
          <HamburgerMenu />
        </div>
        <div className="flex shrink-0 items-center gap-x-6">
          <HelpCenter />
          <Link
            href="/activity"
            className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center text-white/60 no-underline transition-colors duration-150 ease-linear hover:text-white focus:text-white"
          >
            <CountIndicator />
            <BellIcon className="text-white/70 group-hover:text-white group-focus:text-white" />
          </Link>
        </div>
      </Navbar>
      <Banner
        hidden={!maintenance}
        text={
          <BannerContentContainer>
            <span>
              Scheduled maintenance planned: {maintenance?.startDate} - {maintenance?.endDate} AEST
            </span>
            <span style={{ fontSize: '14px' }}>
              Paid platform including access to transaction account will be unavailable during this
              period
            </span>
          </BannerContentContainer>
        }
      />
      <GlobalDialog />
      <GlobalFlyout />
      <GlobalAlertDialog />
      <GlobalConfirmDialog />
      <EvidencePreview />
      {shouldGateForRequiringActions ? (
        <LoadingIndicator className="fixed inset-0 z-50 h-screen w-screen bg-white" />
      ) : (
        children
      )}
    </div>
  );
};
